import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import { ShieldCheck, ShieldOff, QrCode, Loader2, KeyRound, Copy } from "lucide-react";

export default function Security() {
  const { state, refresh } = useAuth();
  const me = state?.user;
  const [step, setStep] = useState("idle");      // idle | enrolling | verifying | done | disabling | regenerating
  const [enrol, setEnrol] = useState(null);      // {secret, provisioning_uri, qr_data_uri}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [regenPwd, setRegenPwd] = useState("");
  const [regenCode, setRegenCode] = useState("");

  useEffect(() => { if (state === null) refresh && refresh(); }, [state, refresh]);

  const startEnrol = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/auth/2fa/enrol`);
      setEnrol(r.data);
      setStep("verifying");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const verifyEnrol = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/auth/2fa/verify`, { code });
      setRecoveryCodes(r.data.recovery_codes);
      setStep("done");
      toast.success("Two-factor authentication is now ON for your account.");
      refresh && refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const disable2FA = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/2fa/disable`, {
        password: disablePwd,
        totp_code: disableCode || undefined,
      });
      toast.success("Two-factor authentication disabled.");
      setStep("idle"); setDisablePwd(""); setDisableCode("");
      refresh && refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const regenerateRecovery = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/auth/2fa/recovery/regenerate`, {
        password: regenPwd,
        totp_code: regenCode,
      });
      setRecoveryCodes(r.data.recovery_codes);
      setRegenPwd(""); setRegenCode("");
      setStep("done");
      toast.success("New recovery codes generated. Save them safely.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copyAll = () => {
    if (!recoveryCodes) return;
    navigator.clipboard?.writeText(recoveryCodes.join("\n"));
    toast.success("Recovery codes copied to clipboard");
  };

  return (
    <div className="px-10 py-12 max-w-3xl" data-testid="security-page">
      <div className="overline mb-2">Settings</div>
      <h1 className="font-heading text-4xl mb-2" style={{ fontWeight: 500 }}>
        Account <em className="italic" style={{ color: "var(--accent)" }}>security</em>
      </h1>
      <p className="text-[var(--ink-2)] mb-10 text-lg">
        Two-factor authentication adds a code from your phone on top of your password — even if your password is leaked, your account stays safe.
      </p>

      <div className="card p-6 mb-6" data-testid="security-2fa-card">
        <div className="flex items-start justify-between gap-6 mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {me?.totp_enabled
                ? <ShieldCheck size={18} style={{ color: "var(--moss)" }} />
                : <ShieldOff size={18} style={{ color: "var(--ink-3)" }} />}
              <h3 className="font-heading text-xl" style={{ fontWeight: 500 }}>
                Two-factor authentication
              </h3>
              <span className="badge text-[9px]"
                    style={{ background: me?.totp_enabled ? "var(--moss)" : "var(--ink-3)", color: "var(--paper)" }}>
                {me?.totp_enabled ? "ENABLED" : "OFF"}
              </span>
            </div>
            <p className="text-[13px] text-[var(--ink-2)]">
              {me?.totp_enabled
                ? `Enrolled ${me?.totp_enrolled_at ? new Date(me.totp_enrolled_at).toLocaleDateString() : ""}. We'll ask for a 6-digit code on every sign-in.`
                : "Recommended for any user who can read client emails or financial data."}
            </p>
          </div>
        </div>

        {step === "idle" && !me?.totp_enabled && (
          <button onClick={startEnrol} disabled={busy} className="btn-primary" data-testid="security-2fa-enrol-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <QrCode size={14} />} Enable 2FA
          </button>
        )}

        {step === "idle" && me?.totp_enabled && (
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => setStep("regenerating")} className="btn-secondary" data-testid="security-2fa-regenerate-btn">
              <KeyRound size={14} /> Regenerate recovery codes
            </button>
            <button onClick={() => setStep("disabling")} className="btn-secondary" data-testid="security-2fa-disable-btn">
              <ShieldOff size={14} /> Disable 2FA
            </button>
          </div>
        )}

        {step === "verifying" && enrol && (
          <form onSubmit={verifyEnrol} className="space-y-5" data-testid="security-2fa-enrol-form">
            <div className="bg-[var(--subtle)] rounded-md p-4 space-y-3">
              <div className="overline">Step 1 · Scan with your authenticator app</div>
              <div className="flex items-start gap-4 flex-wrap">
                <img src={enrol.qr_data_uri} alt="QR code" width={180} height={180}
                     className="border border-[var(--border)] rounded bg-white p-2" data-testid="security-2fa-qr" />
                <div className="text-[12px] text-[var(--ink-2)] max-w-sm">
                  <p>Open <b>Google Authenticator</b>, <b>1Password</b>, <b>Authy</b>, or any TOTP app and scan the QR.</p>
                  <p className="mt-2">Can't scan? Type this secret manually:</p>
                  <code className="kbd font-mono text-[11px] mt-1 block break-all" data-testid="security-2fa-secret">{enrol.secret}</code>
                </div>
              </div>
            </div>
            <div>
              <label className="overline block mb-1.5">Step 2 · Enter the 6-digit code from your app</label>
              <input value={code} onChange={(e) => setCode(e.target.value)}
                     inputMode="numeric" autoComplete="one-time-code"
                     placeholder="123456"
                     data-testid="security-2fa-code"
                     className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[16px] font-mono tracking-widest" />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setStep("idle"); setEnrol(null); setCode(""); }}
                      className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={busy || code.length < 6} className="btn-primary flex-1 justify-center"
                      data-testid="security-2fa-verify-btn">
                {busy ? <Loader2 size={14} className="animate-spin" /> : "Verify & enable"}
              </button>
            </div>
          </form>
        )}

        {step === "done" && recoveryCodes && (
          <div className="bg-[var(--subtle)] rounded-md p-5" data-testid="security-2fa-recovery">
            <div className="flex items-center gap-2 mb-3">
              <KeyRound size={16} style={{ color: "var(--accent)" }} />
              <h4 className="font-heading text-lg" style={{ fontWeight: 500 }}>Save your recovery codes</h4>
            </div>
            <p className="text-[13px] text-[var(--ink-2)] mb-3">
              Store these somewhere safe (a password manager works well).
              Each code works <b>once</b> if you lose your authenticator.
              <span className="text-[var(--accent)]"> You will not see them again.</span>
            </p>
            <div className="grid grid-cols-2 gap-2 font-mono text-[13px] mb-4">
              {recoveryCodes.map((c) => (
                <code key={c} className="kbd text-[12px]">{c}</code>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={copyAll} className="btn-secondary" data-testid="security-2fa-copy-codes">
                <Copy size={14} /> Copy all
              </button>
              <button onClick={() => { setStep("idle"); setEnrol(null); setRecoveryCodes(null); setCode(""); }}
                      className="btn-primary" data-testid="security-2fa-finish">
                I've saved them — done
              </button>
            </div>
          </div>
        )}

        {step === "disabling" && (
          <form onSubmit={disable2FA} className="space-y-4 max-w-sm" data-testid="security-2fa-disable-form">
            <p className="text-[13px] text-[var(--ink-2)]">
              Disabling 2FA weakens your account. Confirm with your password and current 2FA code.
            </p>
            <div>
              <label className="overline block mb-1.5">Current password</label>
              <input type="password" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)}
                     data-testid="security-2fa-disable-password"
                     className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px]" />
            </div>
            <div>
              <label className="overline block mb-1.5">Current 2FA code</label>
              <input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} inputMode="numeric"
                     data-testid="security-2fa-disable-code" placeholder="123456"
                     className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] font-mono" />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setStep("idle")} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={busy || !disablePwd} className="btn-primary flex-1 justify-center"
                      data-testid="security-2fa-disable-confirm">
                {busy ? <Loader2 size={14} className="animate-spin" /> : "Disable 2FA"}
              </button>
            </div>
          </form>
        )}

        {step === "regenerating" && (
          <form onSubmit={regenerateRecovery} className="space-y-4 max-w-sm" data-testid="security-2fa-regenerate-form">
            <p className="text-[13px] text-[var(--ink-2)]">
              This invalidates <b>all</b> of your existing recovery codes and issues a fresh set.
              You'll need your password and a current 2FA code from your authenticator app.
            </p>
            <div>
              <label className="overline block mb-1.5">Current password</label>
              <input type="password" value={regenPwd} onChange={(e) => setRegenPwd(e.target.value)}
                     data-testid="security-2fa-regenerate-password"
                     className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px]" />
            </div>
            <div>
              <label className="overline block mb-1.5">Current 2FA code</label>
              <input value={regenCode} onChange={(e) => setRegenCode(e.target.value)} inputMode="numeric"
                     autoComplete="one-time-code"
                     data-testid="security-2fa-regenerate-code" placeholder="123456"
                     className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] font-mono" />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setStep("idle"); setRegenPwd(""); setRegenCode(""); }}
                      className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={busy || !regenPwd || regenCode.length < 6} className="btn-primary flex-1 justify-center"
                      data-testid="security-2fa-regenerate-confirm">
                {busy ? <Loader2 size={14} className="animate-spin" /> : "Regenerate codes"}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="card p-5 text-[12px] text-[var(--ink-3)]" data-testid="security-info-card">
        <div className="overline mb-2">Security posture</div>
        <ul className="space-y-1 list-disc pl-5">
          <li>Gmail OAuth tokens are <b>encrypted at rest</b> (AES-128-CBC + HMAC).</li>
          <li>Synced email bodies are <b>encrypted at rest</b> too — they're never readable from raw DB dumps.</li>
          <li>Every Gmail view, reply, attach, sync, and disconnect is written to an immutable <b>audit log</b>.</li>
          <li>On disconnect, refresh tokens are <b>revoked at Google</b> so they can't be reused.</li>
          <li>OAuth uses <b>PKCE</b> and we request only <code className="font-mono">gmail.readonly</code> + <code className="font-mono">gmail.send</code> + email — no profile data.</li>
        </ul>
      </div>
    </div>
  );
}
